import { useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { AttributionTracker } from '@/utils/attribution';

/** Set once the source reached Postgres, per account in this browser. */
const SENT_KEY = 'attribution_sent_to_server';

export const useAttributionTracking = () => {
  const { isAuthenticated, user, isLoading, getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    if (isLoading || !isAuthenticated || !user) return;

    const attribution = AttributionTracker.getAttribution();
    if (!attribution) return;

    // PostHog side: once per session is enough, it is an event stream.
    if (!sessionStorage.getItem('attribution_tracked')) {
      AttributionTracker.trackConversion(user.sub ?? user.email ?? 'unknown', 'User Signup');
      sessionStorage.setItem('attribution_tracked', 'true');
    }

    // Postgres side: once per account, and only marked done after it lands, so
    // a failed send (offline, 500) is retried on the next load rather than
    // silently costing us the user's source forever.
    const sentKey = `${SENT_KEY}:${user.sub || user.email}`;
    if (localStorage.getItem(sentKey)) return;

    (async () => {
      try {
        // App.js sets the axios bearer token in its own effect, which can lose
        // the race with this one. Send it explicitly from the Auth0 cache — the
        // endpoint is behind checkJWT and an unauthenticated POST just 401s.
        const token = await getAccessTokenSilently();
        await AttributionTracker.sendConversionToServer('signup', token);
        localStorage.setItem(sentKey, 'true');
      } catch (error) {
        console.error('[attribution] could not persist signup source', error);
      }
    })();
  }, [isAuthenticated, user, isLoading, getAccessTokenSilently]);
};
